import React, { useState } from "react";
import {
  Crown,
  Shield,
  UserCog,
  Stethoscope,
  HeartPulse,
  ClipboardList,
  Pill,
  Calculator,
  Microscope,
  Scan,
  Users,
} from "lucide-react";
import DropdownPortal from "./common/DropdownPortal";
import { useSelector } from "react-redux";
import { arClass } from "../utils/useArClass";
import appRoles from "../utils/constants/appRoles.json";
const normalizeRoleKey = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

const getRoleStyles = (darkMode) => ({
  owner: darkMode
    ? "bg-amber-950/40 text-amber-300 border-amber-800/60"
    : "bg-amber-100 text-amber-800 border-amber-200",

  admin: darkMode
    ? "bg-sky-950/40 text-sky-300 border-sky-800/60"
    : "bg-sky-100 text-sky-800 border-sky-200",

  subadmin: darkMode
    ? "bg-indigo-950/40 text-indigo-300 border-indigo-800/60"
    : "bg-indigo-100 text-indigo-800 border-indigo-200",

  doctor: darkMode
    ? "bg-emerald-950/40 text-emerald-300 border-emerald-800/60"
    : "bg-emerald-100 text-emerald-800 border-emerald-200",

  nurse: darkMode
    ? "bg-teal-950/40 text-teal-300 border-teal-800/60"
    : "bg-teal-100 text-teal-800 border-teal-200",

  receptionist: darkMode
    ? "bg-rose-950/40 text-rose-300 border-rose-800/60"
    : "bg-rose-100 text-rose-800 border-rose-200",

  pharmacist: darkMode
    ? "bg-violet-950/40 text-violet-300 border-violet-800/60"
    : "bg-violet-100 text-violet-800 border-violet-200",

  accountant: darkMode
    ? "bg-orange-950/40 text-orange-300 border-orange-800/60"
    : "bg-orange-100 text-orange-800 border-orange-200",

  lab_technician: darkMode
    ? "bg-lime-950/40 text-lime-300 border-lime-800/60"
    : "bg-lime-100 text-lime-800 border-lime-200",

  radiologist: darkMode
    ? "bg-cyan-950/40 text-cyan-300 border-cyan-800/60"
    : "bg-cyan-100 text-cyan-800 border-cyan-200",

  staff: darkMode
    ? "bg-slate-800/70 text-slate-300 border-slate-700"
    : "bg-slate-100 text-slate-700 border-slate-200",
});


const roleIcons = {
  owner: Crown,
  admin: Shield,
  subadmin: UserCog,
  doctor: Stethoscope,
  nurse: HeartPulse,
  receptionist: ClipboardList,
  pharmacist: Pill,
  accountant: Calculator,
  lab_technician: Microscope,
  radiologist: Scan,
  staff: Users,
};

const getRoleTitle = (role, language) => {
  const roleId = role?.name || role;
  const roleKey = normalizeRoleKey(roleId);
  const fallbackLanguage = appRoles.en ? "en" : Object.keys(appRoles)[0];
  const selectedLanguage = appRoles[language] ? language : fallbackLanguage;
  const localizedRoles = appRoles[selectedLanguage]?.baseRoles || [];
  const fallbackRoles = appRoles[fallbackLanguage]?.baseRoles || [];

  const localizedRole = localizedRoles.find(
    (entry) => normalizeRoleKey(entry.id) === roleKey,
  );
  const fallbackRole = fallbackRoles.find(
    (entry) => normalizeRoleKey(entry.id) === roleKey,
  );

  return localizedRole?.title || fallbackRole?.title || roleId;
};

const RoleBadge = ({
  role,
  style = "default",
  paddingY = "py-1",
  paddingX = "px-3",
  fontSize = "text-xs",
  smFontSize = "max-sm:text-xs",
  shortTheVisibleRoles = false,
  lengthOfVCIVR = 5,
}) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const roleId = role?.id || role?.name || role;
  const roleLabel = getRoleTitle(role, defaultLanguage);
  const roleKey = normalizeRoleKey(roleId);
  const Icon = roleIcons[roleKey] || Users;
  const roleStyles = getRoleStyles(darkMode);
const baseStyleClass = roleStyles[roleKey] || roleStyles.staff;
  const styleVariants = {
    default: `border ${baseStyleClass}`,
    "bg-text": `border-0 ${baseStyleClass}`,
    "text-only": "border-0 bg-transparent text-slate-700",
  };
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const styleClass = styleVariants[style] || styleVariants.default;
  let shortenedRoleLabel = roleLabel;
  if (shortTheVisibleRoles && roleLabel.length > lengthOfVCIVR) {
    shortenedRoleLabel = roleLabel.slice(0, lengthOfVCIVR) + "...";
  }
  return (
    <div
      className={`inline-flex items-center gap-0.5 rounded-full ${paddingX} ${paddingY}  ${smFontSize} font-semibold w-max ${styleClass}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span
        className={arClass("relative")}
        onMouseEnter={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          setIsOpen(false);
          setAnchorEl(null);
        }}
      >
        <span
          className={arClass(
            `cursor-pointer ${fontSize}`,
          )}
          aria-expanded={isOpen}
        >
          {shortenedRoleLabel}
        </span>
        {isOpen && anchorEl && shortTheVisibleRoles && roleLabel.length > lengthOfVCIVR && (
          <DropdownPortal
            anchorRef={{ current: anchorEl }}
            onClose={() => {
              setIsOpen(false);
              setAnchorEl(null);
            }}
            className="!w-max  "
          >
            <div className={arClass("p-2")}>

              <div

                className={arClass(
                  `px-2 py-1 rounded text-xs w-full ${darkMode
                    ? "text-gray-200 hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
                  }`,
                )}
              >
                <div
                  className={`inline-flex items-center gap-2 rounded-full ${paddingX} ${paddingY} ${fontSize} ${smFontSize} font-semibold w-max ${styleClass}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {roleLabel}
                </div>
              </div>
            </div>
          </DropdownPortal>
        )}
      </span>
    </div>
  );
};

export default RoleBadge;